import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  Wallet, 
  Users, 
  CheckCircle2, 
  XCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { currencySymbols } from '@/types';

interface DuesSummary {
  totalMembers: number;
  paidCount: number;
  owingCount: number;
  creditCount: number;
  totalCollected: number;
  totalOutstanding: number;
  totalCredit: number;
}

interface MemberDues {
  id: string;
  full_name: string;
  title?: string;
  status: 'active' | 'inactive';
  dues_balance: number;
  credit_balance: number;
  last_payment?: string;
}

export default function Dues() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useAppStore();
  
  const [members, setMembers] = useState<MemberDues[]>([]);
  const [summary, setSummary] = useState<DuesSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  const monthlyDues = currentOrganization?.monthly_dues_amount || 0;

  useEffect(() => {
    fetchDuesData();
  }, [currentOrganization]);

  const fetchDuesData = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      // Query 1: Fetch all members
      const { data: membersData, error: membersError } = await supabase
        .from('members')
        .select('id, full_name, title, status, dues_balance, credit_balance')
        .eq('organization_id', currentOrganization.id)
        .order('full_name');

      if (membersError) throw membersError;

      // FIX #1 & #2: Single batch fetch for all dues payments —
      // replaces the previous N+1 pattern (one query per member).
      // We also use this same data to compute totalCollected below,
      // eliminating the separate third query that existed before.
      const { data: allPaymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('member_id, payment_date, amount')
        .eq('organization_id', currentOrganization.id)
        .eq('payment_type', 'dues')
        .order('payment_date', { ascending: false });

      if (paymentsError) throw paymentsError;

      // Build member_id → latest payment_date map in one pass
      const lastPaymentMap = new Map<string, string>();
      for (const p of allPaymentsData || []) {
        // Because results are ordered descending, first entry per member
        // is the most recent — skip subsequent entries for the same member.
        if (!lastPaymentMap.has(p.member_id)) {
          lastPaymentMap.set(p.member_id, p.payment_date);
        }
      }

      // Merge last_payment into each member without any extra DB calls
      const membersWithPayments: MemberDues[] = (membersData || []).map((member) => ({
        ...member,
        last_payment: lastPaymentMap.get(member.id),
      }));

      setMembers(membersWithPayments);

      // FIX #4: Scope financial totals to active members only so that
      // deactivated members' balances don't distort the overview numbers.
      const activeMembers = membersWithPayments.filter((m) => m.status === 'active');

      // FIX #3: A member is only "paid" when they have at least one
      // recorded payment AND currently owe nothing. Brand-new members
      // who have never paid (last_payment == null, dues_balance == 0)
      // were previously miscounted as paid — they are now excluded.
      const paidCount = activeMembers.filter(
        (m) => m.last_payment != null && m.dues_balance === 0
      ).length;

      const owingCount = activeMembers.filter((m) => m.dues_balance > 0).length;
      const creditCount = activeMembers.filter((m) => m.credit_balance > 0).length;

      // FIX #4 (continued): reduce over activeMembers, not all members
      const totalOutstanding = activeMembers.reduce((sum, m) => sum + (m.dues_balance || 0), 0);
      const totalCredit = activeMembers.reduce((sum, m) => sum + (m.credit_balance || 0), 0);

      // FIX #2: Reuse the already-fetched payments batch instead of
      // issuing a separate query just to sum amounts.
      const totalCollected = (allPaymentsData || []).reduce(
        (sum, p) => sum + (p.amount || 0),
        0
      );

      setSummary({
        totalMembers: activeMembers.length,
        paidCount,
        owingCount,
        creditCount,
        totalCollected,
        totalOutstanding,
        totalCredit,
      });
    } catch (error) {
      console.error('Error fetching dues data:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  // FIX #5: Distinguish truly paid members from brand-new members who
  // have never made any payment (both have zero balances but mean
  // completely different things).
  const getMemberStatus = (
    member: MemberDues
  ): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' } => {
    if (member.credit_balance > 0) return { label: 'Credit', variant: 'default' };
    if (member.dues_balance > 0) return { label: 'Owing', variant: 'destructive' };
    if (member.last_payment != null) return { label: 'Paid', variant: 'secondary' };
    // Zero balance but no payment on record → new / never paid
    return { label: 'New', variant: 'outline' };
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('navigation.dues')}</h1>
          <p className="text-muted-foreground">
            Monthly dues: {currencySymbol}{monthlyDues}
          </p>
        </div>
        <Button onClick={() => navigate('/dues/collect')}>
          <Plus className="w-4 h-4 mr-2" />
          {t('dues.collect')}
        </Button>
      </div>

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Members"
            value={summary.totalMembers}
            icon={Users}
            isLoading={isLoading}
          />
          <SummaryCard
            title="Paid"
            value={summary.paidCount}
            subtitle={`${Math.round((summary.paidCount / summary.totalMembers) * 100) || 0}%`}
            icon={CheckCircle2}
            trend="up"
            isLoading={isLoading}
          />
          <SummaryCard
            title="Owing"
            value={summary.owingCount}
            icon={XCircle}
            trend="down"
            isLoading={isLoading}
          />
          <SummaryCard
            title="With Credit"
            value={summary.creditCount}
            icon={TrendingUp}
            trend="up"
            isLoading={isLoading}
          />
        </div>
      ) : null}

      {/* Financial Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Financial Overview</CardTitle>
          <CardDescription>Dues collection summary</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Collected</p>
              <p className="text-xl font-bold text-green-600">
                {isLoading ? <Skeleton className="h-7 w-20" /> : 
                  `${currencySymbol}${summary?.totalCollected.toLocaleString() || 0}`}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Outstanding</p>
              <p className="text-xl font-bold text-red-600">
                {isLoading ? <Skeleton className="h-7 w-20" /> : 
                  `${currencySymbol}${summary?.totalOutstanding.toLocaleString() || 0}`}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Credit Balance</p>
              <p className="text-xl font-bold text-blue-600">
                {isLoading ? <Skeleton className="h-7 w-20" /> : 
                  `${currencySymbol}${summary?.totalCredit.toLocaleString() || 0}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Member Dues Status</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : members.length > 0 ? (
            <div className="space-y-2">
              {members.map((member) => {
                const status = getMemberStatus(member);
                return (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{member.full_name}</p>
                        <p className="text-sm text-muted-foreground">{member.title}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        {member.dues_balance > 0 && (
                          <p className="text-sm text-red-600">
                            Owes: {currencySymbol}{member.dues_balance}
                          </p>
                        )}
                        {member.credit_balance > 0 && (
                          <p className="text-sm text-green-600">
                            Credit: {currencySymbol}{member.credit_balance}
                          </p>
                        )}
                        {member.dues_balance === 0 && member.credit_balance === 0 && member.last_payment != null && (
                          <p className="text-sm text-muted-foreground">Up to date</p>
                        )}
                        {member.dues_balance === 0 && member.credit_balance === 0 && member.last_payment == null && (
                          <p className="text-sm text-muted-foreground">No payments yet</p>
                        )}
                      </div>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No members found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ElementType;
  trend?: 'up' | 'down';
  isLoading: boolean;
}

function SummaryCard({ title, value, subtitle, icon: Icon, trend, isLoading }: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-8 w-16 mt-1" />
            ) : (
              <p className="text-2xl font-bold">{value}</p>
            )}
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-1">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            {trend && (
              trend === 'up' ? 
                <TrendingUp className="w-3 h-3 text-green-500" /> : 
                <TrendingDown className="w-3 h-3 text-red-500" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
